import * as React from "react";
import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { Outlet, useNavigate } from "react-router-dom";
import { logoutFromServer } from "../../utils/request";
import theme from "../../theme/theme";
import RscLogo from "../../assests/logo/rscLogo copy";
import {
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import BreadcrumbComponent from "../CustomBreadcurmComponent";
import { getUsers, isDiscordIsVerified } from "../../utils/getUser";
import { useEffect } from "react";
import { base64ToFile, formatNumberToDollar } from "../../utils/util";
import useNavigation, { useRouter } from "./Navigation";
import { useContext } from "react";
import { AuthContext } from "../../context/authContext";
import { useDispatch } from "react-redux";
import { getWalletSummaryDetail } from "../../reducers/wallet.slice";
import { WalletLogo } from "../../assests/logo/walletLogo";

const PageContainer = styled("div")(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: "10px",
}));

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "left",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 200,
    maxWidth: "95vw", // Prevent menu from overflowing on mobile
    color: theme.palette.text.primary,
    boxShadow:
      "rgb(255 255 255 / 20%) 0px 0px 0px 1px, rgb(0 0 0 / 20%) 0px 2px 10px",
    "& .MuiList-root": {
      padding: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1),
      },
    },
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  "&.MuiMenuItem-root": {
    padding: theme.spacing(1, 2),
    margin: theme.spacing(1, 0),
    "&:last-child": {
      marginBottom: 0,
    },
    borderRadius: "10px",
    border: "1px solid rgb(102, 102, 102)",
    textAlign: "center",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const CustomHr = styled("hr")(({ theme }) => ({
  border: "1px solid rgb(102, 102, 102)",
  borderRadius: "10px",
  width: "90%",
  height: "1px",
  margin: "20px auto 15px auto",
}));

const roleIcons = {
  member: "👶",
  trusted: "🫰",
  whale: "🐋",
  beluga: "🐳",
};

const StyledRoleBox = styled(Box)(({ theme, role }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "8px",
  borderRadius: "8px",
  border: "2px solid",
  gap: 2,
  borderColor: role
    ? theme.palette.memberColorsBG[role]
    : "rgba(121, 121, 121, 0.2)",
}));

const WalletBalanceWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  padding: "8px",
  borderRadius: "8px",
  border: "2px solid #2B2477",
  background: "linear-gradient(135deg, #302883CC 0%, #06050E 100%)",
  marginRight: "10px",
  cursor: "pointer",
}));

function ToolbarActionsSearch() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { walletSummary, setWalletSummary, isWalletRefresh, showToast } =
    useContext(AuthContext);

  const user = getUsers();

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  function stringToColor(string) {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  }

  function stringAvatar(name) {
    return {
      sx: {
        bgcolor: stringToColor(name),
        color: "white",
        border: "2px solid white",
        fontSize: "15px",
      },
      children: `${name.split(" ")?.[0]?.[0]}${name.split(" ")?.[1]?.[0]}`,
    };
  }

  const discordRoleBasedColor = user?.discord_role?.[0]?.replace(" ", "");

  let isDiscord = isDiscordIsVerified();

  useEffect(() => {
    const payload = { userId: user?.id };
    dispatch(getWalletSummaryDetail(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setWalletSummary(res?.payload);
    });
  }, [isWalletRefresh]);

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Tooltip title="Search" enterDelay={1000}></Tooltip>

        {user?.role === "MEMBER" && (
          <WalletBalanceWrapper
            gap={1}
            onClick={() => navigate("/dashboard/wallet")}
          >
            <WalletLogo width="20" height="20" />
            <Typography>
              {formatNumberToDollar(walletSummary?.current_balance)}
            </Typography>
          </WalletBalanceWrapper>
        )}

        {user?.role === "MEMBER" && !isMobile && (
          <StyledRoleBox
            role={user?.discord_role?.length > 0 ? discordRoleBasedColor : null}
          >
            {user?.discord_role && (
              <Typography
                sx={{
                  fontSize: "15px",
                  color:
                    user?.discord_role?.length > 0
                      ? `memberColors.${discordRoleBasedColor}`
                      : "white",
                  textTransform: "capitalize",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {user?.discord_role?.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ mr: 1, fontSize: "1rem" }}>
                      {roleIcons[user.discord_role[0]?.toLowerCase()] || ""}
                    </Typography>
                    <Typography variant="body1">
                      {user.discord_role[0]
                        ?.toLowerCase()
                        .replace(/^\w/, (c) => c.toUpperCase())}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1">No Role</Typography>
                )}
              </Typography>
            )}
          </StyledRoleBox>
        )}
        <Box sx={{ marginLeft: 2 }}>
          <Tooltip title="Open settings">
            {user?.profile_picture ? (
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt={user?.first_name}>
                  <img
                    src={URL.createObjectURL(
                      base64ToFile(user?.profile_picture, "file", "image/*")
                    )}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      backgroundSize: "ellipsis",
                      backgroundRepeat: "no-repeat",
                    }}
                    alt=""
                  />
                </Avatar>
              </IconButton>
            ) : (
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  {...stringAvatar(user?.first_name + " " + user?.last_name)}
                />
              </IconButton>
            )}
          </Tooltip>
          <StyledMenu
            sx={{
              padding: "0px 10px !important",
              "& .MuiMenu-paper": {
                width: {
                  xs: "calc(100% - 32px)", // Full width minus margins on mobile
                  sm: "auto", // Default width on desktop
                },
              },
            }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                width: "100%",
                p: { xs: 1, sm: 2 },
              }}
            >
              {user?.profile_picture ? (
                <IconButton onClick={handleCloseUserMenu} sx={{ p: 0 }}>
                  <Avatar alt={user?.first_name}>
                    <img
                      src={URL.createObjectURL(
                        base64ToFile(user?.profile_picture, "file", "image/*")
                      )}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                      }}
                      alt=""
                    />
                  </Avatar>
                </IconButton>
              ) : (
                <IconButton onClick={handleCloseUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    {...stringAvatar(user?.first_name + " " + user?.last_name)}
                  />
                </IconButton>
              )}
              <Stack
                sx={{
                  flex: 1,
                  backgroundColor: "transparent",
                  padding: 0,
                }}
                direction="column"
                alignItems="flex-start"
              >
                <Typography
                  sx={{
                    width: "100%",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  {user?.first_name + " " + user?.last_name}
                </Typography>
                <Typography
                  sx={{
                    width: "100%",
                    maxWidth: "200px",
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    overflow: "hidden",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    lineHeight: "1.2",
                  }}
                >
                  {user?.email}
                </Typography>
              </Stack>
            </Stack>
            <CustomHr />
            <StyledMenuItem
              onClick={() => {
                handleCloseUserMenu();
                navigate("/dashboard/my-profile");
              }}
              disabled={!isDiscord && user?.role === "MEMBER"}
            >
              <Typography width="100%" textAlign="center">
                Profile
              </Typography>
            </StyledMenuItem>

            <StyledMenuItem
              onClick={async () => {
                handleCloseUserMenu();
                await logoutFromServer();
                sessionStorage.clear();
                localStorage.clear();
                const logoutChannel = new BroadcastChannel("logout_channel");
                logoutChannel.postMessage("logout");
                navigate("/");
              }}
            >
              <Typography width="100%" textAlign="center">
                Logout
              </Typography>
            </StyledMenuItem>
          </StyledMenu>
        </Box>
      </Stack>
    </>
  );
}

function CustomAppTitle({ userRole }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <RscLogo width="30px" height="17px" fill="white" color="black" />
      <Typography variant="h6">
        {userRole === "member" ? "RSC" : "RSC Management"}
      </Typography>
    </Stack>
  );
}

export default function DashboardLayoutBasic() {
  const userDetails = getUsers();
  const userRole = userDetails?.role?.toLowerCase();
  const [user, setUser] = React.useState({});
  const router = useRouter();

  useEffect(() => {
    const user = getUsers();
    setUser(user);
  }, []);

  return (
    <AppProvider
      session={user}
      navigation={useNavigation() || []}
      branding={{
        homeUrl: "/login",
      }}
      router={router}
      theme={theme}
    >
      <DashboardLayout
        slots={{
          appTitle: () => CustomAppTitle({ userRole }),
          toolbarActions: ToolbarActionsSearch,
        }}
      >
        <PageContainer>
          <BreadcrumbComponent />
          <Outlet />
        </PageContainer>
      </DashboardLayout>
    </AppProvider>
  );
}
